import { useGlobalAppState } from "@/utils/app-state";
import { useDANGEROUSisChalkEmployee } from "@/utils/hooks/use-is-chalk-employee";
import { isMac } from "@/utils/platform-utils";
import { useEffect } from "react";
import toast from "react-hot-toast";

/**
 * Global mod+shift+D hotkey (chalk employees only) that toggles demo mode: admin-only
 * components stay visible, but the red "chalk admin" visual treatment is suppressed so
 * the dashboard can be screen-shared with customers.
 *
 * Renders nothing; mount once near GlobalAppStateListener in _app.
 */
export const DemoModeHotkey: React.FC = () => {
  const {
    state: { demoMode },
    updateState,
  } = useGlobalAppState();
  // ignoreAdminMode: the hotkey must work even when adminMode is toggled off, and must
  // not become active for non-employees just because demoMode is set in local storage.
  const isChalkEmployee = useDANGEROUSisChalkEmployee({ ignoreAdminMode: true });

  useEffect(() => {
    // The CSS override in styles/admin-demo-mode.css keys off this root class.
    document.documentElement.classList.toggle("chalk-demo-mode", demoMode && isChalkEmployee);
  }, [demoMode, isChalkEmployee]);

  useEffect(() => {
    if (!isChalkEmployee) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      const modPressed = isMac() ? e.metaKey : e.ctrlKey;
      // e.key is "D" (uppercase) when shift is held; check both for safety across layouts.
      if (modPressed && e.shiftKey && (e.key === "D" || e.key === "d")) {
        // Block the browser's own binding (e.g. "bookmark all tabs").
        e.preventDefault();
        updateState({ demoMode: !demoMode });
        toast(!demoMode ? "Demo mode on" : "Demo mode off", { id: "chalk-demo-mode" });
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isChalkEmployee, demoMode, updateState]);

  return null;
};
